import { FeedPlayerInfo, LeagueOutput, PlayOutput, RatingHistory } from '../../generated-api';

export const formatRelativeTime = (dateString: string, now: Date = new Date()) => {
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export const formatDuration = (minutes?: number | null) => {
  if (!minutes || minutes <= 0) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

export interface FeedPlayResult {
  /** 1-based place; ties share a place. Null when the play has no ranking for this side. */
  place: number | null;
  players: FeedPlayerInfo[];
}

/** What one play did to a player's Global rating. */
export interface FeedRatingImpact {
  /** Rating (mu) gained or lost in this play. */
  delta: number;
  /** Global position just before / after the play; null while the rating is still uncertain. */
  rankBefore: number | null;
  rankAfter: number | null;
}

export interface FeedPlayDetails {
  playDate: string;
  durationMinutes: number | null;
  leagueNames: string[];
  results: FeedPlayResult[];
  /** By player ID; only players the play rated in Global. */
  ratingImpacts?: Record<string, FeedRatingImpact>;
}

// Same certainty threshold as the league table: 5+ games and sigma at most 5.
const MIN_GAMES_FOR_RANK = 5;
const MAX_SIGMA_FOR_RANK = 5;

/**
 * Order every play in the rating histories. Each player's history is already in
 * replay order, so a topological merge of those lists (earliest playDate first
 * when free) gives an order consistent with the backend replay.
 */
const orderPlays = (histories: RatingHistory[]): Map<string, number> => {
  const date = new Map<string, number>();
  const next = new Map<string, Set<string>>();
  const indegree = new Map<string, number>();
  for (const h of histories) {
    const entries = h.ratingProgression || [];
    entries.forEach((e, i) => {
      if (!date.has(e.playId)) {
        date.set(e.playId, new Date(e.playDate).getTime());
        indegree.set(e.playId, 0);
        next.set(e.playId, new Set());
      }
      if (i > 0) {
        const prev = entries[i - 1].playId;
        if (prev !== e.playId && !next.get(prev)!.has(e.playId)) {
          next.get(prev)!.add(e.playId);
          indegree.set(e.playId, (indegree.get(e.playId) || 0) + 1);
        }
      }
    });
  }
  const order = new Map<string, number>();
  const ready = Array.from(indegree.keys()).filter(id => indegree.get(id) === 0);
  while (ready.length > 0) {
    ready.sort((a, b) => date.get(b)! - date.get(a)!);
    const id = ready.pop()!;
    order.set(id, order.size);
    next.get(id)!.forEach(n => {
      indegree.set(n, indegree.get(n)! - 1);
      if (indegree.get(n) === 0) ready.push(n);
    });
  }
  return order;
};

/**
 * Rating change and Global position before/after for every player of the given
 * plays, from all Global rating histories. Positions rank only players past the
 * certainty threshold, by conservative rating (mu - 3 sigma), like the league table.
 */
export const computeRatingImpacts = (
  playIds: string[],
  histories: RatingHistory[],
): Record<string, Record<string, FeedRatingImpact>> => {
  const order = orderPlays(histories);
  // Per player: [play order, conservative rating, sigma, games played] after each play
  const timelines = histories.map(h =>
    (h.ratingProgression || []).map((e, i) => ({
      at: order.get(e.playId) ?? -1,
      playId: e.playId,
      conservative: e.ratingAfter.rating - 3 * e.ratingAfter.sigma,
      certain: i + 1 >= MIN_GAMES_FOR_RANK && e.ratingAfter.sigma <= MAX_SIGMA_FOR_RANK,
      delta: e.ratingChange,
    })),
  );

  const standings = (cutoff: number) => {
    const ranked: { playerId: string; conservative: number }[] = [];
    histories.forEach((h, i) => {
      const line = timelines[i];
      let last: typeof line[number] | undefined;
      for (const e of line) {
        if (e.at <= cutoff) last = e;
        else break;
      }
      if (last?.certain) ranked.push({ playerId: h.playerId, conservative: last.conservative });
    });
    ranked.sort((a, b) => b.conservative - a.conservative);
    return new Map(ranked.map((r, i) => [r.playerId, i + 1]));
  };

  const result: Record<string, Record<string, FeedRatingImpact>> = {};
  for (const playId of playIds) {
    const at = order.get(playId);
    if (at === undefined) continue;
    const before = standings(at - 1);
    const after = standings(at);
    const impacts: Record<string, FeedRatingImpact> = {};
    histories.forEach((h, i) => {
      const entry = timelines[i].find(e => e.playId === playId);
      if (!entry) return;
      impacts[h.playerId] = {
        delta: entry.delta,
        rankBefore: before.get(h.playerId) ?? null,
        rankAfter: after.get(h.playerId) ?? null,
      };
    });
    result[playId] = impacts;
  }
  return result;
};

/**
 * Turn a stored play into what the feed card shows: one result row per team
 * in finishing order (ties share a place), plus date, duration and league names.
 */
export const buildPlayDetails = (
  play: PlayOutput,
  players: FeedPlayerInfo[],
  leagues: LeagueOutput[] = [],
): FeedPlayDetails => {
  const byId = new Map(players.map(p => [p.playerId, p]));
  const teams = play.teams || {};
  const seen = new Set<string>();
  const results: FeedPlayResult[] = [];

  const teamRow = (team: string, place: number | null): FeedPlayResult => {
    seen.add(team);
    return {
      place,
      players: (teams[team] || [])
        .map(id => byId.get(id) || { playerId: id, playerName: '?' }),
    };
  };

  let position = 1;
  for (const entry of play.ranking || []) {
    const tied = Array.isArray(entry) ? entry : [entry];
    for (const team of tied) results.push(teamRow(team, position));
    position += tied.length;
  }
  for (const team of Object.keys(teams)) {
    if (!seen.has(team)) results.push(teamRow(team, null));
  }

  const leagueNames = (play.leagueIds || [])
    .map(id => leagues.find(l => l.id === id))
    // Every play counts towards Global, so naming it adds nothing.
    .filter((league): league is LeagueOutput => !!league && !league.isGlobal)
    .map(league => league.name);

  return {
    playDate: play.playDate,
    durationMinutes: play.durationMinutes ?? null,
    leagueNames,
    results: results.filter(r => r.players.length > 0),
  };
};
